import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useReactivateMyMembershipContext } from '../ReactivateContext';
import useSelectedPlan from '../../hooks/useSelectedPlan';
import useGetPricesByPlan from '../../hooks/useGetPricesByPlan';
import usePriceCalculator from '../../../hooks/usePriceCalculator';
import { IPlanCode } from '../../Interfaces';
import { useUniversalCouponInput } from '@fff-web/fff-payments';
import { getParamFromUrl, useUserContext } from '@fff-web/fff-utilities';

/* ------------------------------------------------------------------------------------------------
 * Interfaces / Types
 * ----------------------------------------------------------------------------------------------- */

export interface CouponResponseData {
  code: string;
  couponType: string;
  discountAmountInCents: number | null;
  discountPercent: number | null;
  gwpRefillProductName?: string | null;
  gwpRefillProductPriceInCents?: string | null;
  previewDescription: string;
  balanceInCents?: number;
}

type UseUniversalCouponInputReturnType = ReturnType<
  typeof useUniversalCouponInput
>;
export interface ReactivatePlanDataContextReturn
  extends Partial<UseUniversalCouponInputReturnType> {
  selectedPlanCode: IPlanCode;
  onPlanChange(planCode: IPlanCode): void;
  basePrice: number;
  shippingPrice: number;
  taxPrice: number;
  discount: number;
  totalPrice: number;
}

/* ------------------------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------------------- */

const COUPON_CODE = 'coupon_code';
const DEFAULT_PLAN_CODE = IPlanCode.SEASONAL;
const REACTIVATE_PLAN_DATA_INITIAL_STATE: ReactivatePlanDataContextReturn = {
  selectedPlanCode: DEFAULT_PLAN_CODE,
  onPlanChange: () => null,
  basePrice: 0,
  shippingPrice: 0,
  taxPrice: 0,
  discount: 0,
  totalPrice: 0,
};

/* ------------------------------------------------------------------------------------------------
 * Create Context
 * ----------------------------------------------------------------------------------------------- */

export const ReactivatePlanDataContext =
  createContext<ReactivatePlanDataContextReturn>(
    REACTIVATE_PLAN_DATA_INITIAL_STATE
  );

/* ------------------------------------------------------------------------------------------------
 * Define Provider
 * ----------------------------------------------------------------------------------------------- */

export const ReactivatePlanDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { selectedPlanCode, setSelectedPlanCode } = useSelectedPlan();
  const { growthMyMembership } = useReactivateMyMembershipContext();
  const { isLoadingPrices, seasonalPrice, annualPrice } =
    useGetPricesByPlan(growthMyMembership);
  const {
    userInfo: { user },
  } = useUserContext();
  const { shippingZipCode, shippingState, shippingCountry } = user;

  const {
    couponData,
    error,
    handleCallCouponApi,
    loading,
    coupon,
    handleOnChange,
    promoCodeType,
  } = useUniversalCouponInput({
    initialCouponCode: getParamFromUrl(COUPON_CODE),
    plan: selectedPlanCode,
    postalCode: shippingZipCode,
    state: shippingState,
    country: shippingCountry,
  });

  let basePrice;

  if (isLoadingPrices) {
    basePrice = 0;
  } else {
    basePrice =
      selectedPlanCode === IPlanCode.SEASONAL
        ? seasonalPrice.price
        : annualPrice.annualPrice;
  }

  const discount = useMemo(
    () => calculateDiscount(basePrice, couponData),
    [basePrice, couponData]
  );

  const {
    tax: taxPrice,
    shipping: shippingPrice,
    total: totalPrice,
  } = usePriceCalculator(selectedPlanCode, basePrice, discount);

  return (
    <ReactivatePlanDataContext.Provider
      value={{
        // Plan code state
        selectedPlanCode,
        onPlanChange: setSelectedPlanCode,

        // Prices
        basePrice,
        discount,
        taxPrice,
        shippingPrice,
        totalPrice,

        // UniversalCouponInput state
        couponData,
        handleCallCouponApi,
        promoCodeType,
        coupon,
        loading,
        handleOnChange,
        error,
      }}
    >
      {children}
    </ReactivatePlanDataContext.Provider>
  );
};

/* ------------------------------------------------------------------------------------------------
 * Hook to consume ReactivatePlanDataContext
 * ----------------------------------------------------------------------------------------------- */

export const useReactivatePlanDataContext = () => {
  const context = useContext(ReactivatePlanDataContext);
  if (!context) {
    throw new Error(
      'You must use the ReactivatePlanDataContext in order to consume this context.'
    );
  }
  return context;
};

/* ------------------------------------------------------------------------------------------------
 * Utils
 * ----------------------------------------------------------------------------------------------- */

export function calculateDiscount(
  basePrice: number,
  couponData: CouponResponseData | null
) {
  if (!couponData) {
    return 0;
  }

  const { discountAmountInCents, discountPercent, balanceInCents } = couponData;

  const discountInCents = discountAmountInCents || balanceInCents;

  if (discountInCents) {
    return parseFloat((discountInCents / 100).toFixed(2));
  }

  if (discountPercent !== null) {
    return parseFloat(((basePrice * discountPercent) / 100).toFixed(2));
  }

  return 0;
}
